import { Node, mergeAttributes } from '@tiptap/core'
import { Plugin } from '@tiptap/pm/state'

export interface SourceCitationOptions {
  HTMLAttributes: Record<string, any>
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    sourceCitation: {
      /**
       * Insert a source citation
       */
      insertSourceCitation: (attributes: {
        sourceType: string
        sourceId: string
        sourceTitle: string
        snippet?: string
        number?: number
      }) => ReturnType
    }
  }
}

export const SourceCitation = Node.create<SourceCitationOptions>({
  name: 'sourceCitation',

  group: 'inline',

  inline: true,

  atom: true,

  addOptions() {
    return {
      HTMLAttributes: {},
    }
  },

  addAttributes() {
    return {
      sourceType: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-source-type'),
        renderHTML: (attributes) => {
          if (!attributes.sourceType) {
            return {}
          }
          return {
            'data-source-type': attributes.sourceType,
          }
        },
      },
      sourceId: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-source-id'),
        renderHTML: (attributes) => {
          if (!attributes.sourceId) {
            return {}
          }
          return {
            'data-source-id': attributes.sourceId,
          }
        },
      },
      sourceTitle: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-source-title'),
        renderHTML: (attributes) => {
          if (!attributes.sourceTitle) {
            return {}
          }
          return {
            'data-source-title': attributes.sourceTitle,
          }
        },
      },
      snippet: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-snippet'),
        renderHTML: (attributes) => {
          if (!attributes.snippet) {
            return {}
          }
          return {
            'data-snippet': attributes.snippet,
          }
        },
      },
      number: {
        default: null,
        parseHTML: (element) => {
          const num = element.getAttribute('data-number')
          return num ? parseInt(num, 10) : null
        },
        renderHTML: (attributes) => {
          if (!attributes.number) {
            return {}
          }
          return {
            'data-number': attributes.number,
          }
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'sup[data-source-citation]',
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'sup',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        'data-source-citation': '',
        class: 'source-citation',
      }),
      `${HTMLAttributes['data-number'] || '?'}`,
    ]
  },

  addCommands() {
    return {
      insertSourceCitation:
        (attributes) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: attributes,
          })
        },
    }
  },

  addProseMirrorPlugins() {
    return [
      // Plugin to renumber citations based on sourceId
      new Plugin({
        appendTransaction: (transactions, oldState, newState) => {
          // Only run if document changed
          const docChanged = transactions.some(tr => tr.docChanged)
          if (!docChanged) return null

          const { doc, schema } = newState
          const citationType = schema.nodes.sourceCitation
          if (!citationType) return null

          // Collect all citations and build sourceId -> number mapping
          const sourceIdToNumber = new Map<string, number>()
          let nextNumber = 1

          // First pass: assign numbers in order of appearance
          doc.descendants((node, pos) => {
            if (node.type === citationType) {
              const sourceId = node.attrs.sourceId
              if (sourceId && !sourceIdToNumber.has(sourceId)) {
                sourceIdToNumber.set(sourceId, nextNumber++)
              }
            }
          })

          // Second pass: update citations with wrong numbers
          const tr = newState.tr
          let changed = false

          doc.descendants((node, pos) => {
            if (node.type === citationType) {
              const sourceId = node.attrs.sourceId
              const correctNumber = sourceIdToNumber.get(sourceId)

              if (correctNumber && node.attrs.number !== correctNumber) {
                tr.setNodeMarkup(pos, undefined, {
                  ...node.attrs,
                  number: correctNumber,
                })
                changed = true
              }
            }
          })

          return changed ? tr : null
        },
      }),
      // Click handler plugin
      new Plugin({
        props: {
          handleClick(view, pos) {
            const { schema, doc } = view.state
            const node = doc.nodeAt(pos)

            if (node?.type.name === 'sourceCitation') {
              // Select the citation node when clicked
              const { tr } = view.state
              const nodePos = pos - (pos - doc.resolve(pos).parentOffset)
              const selection = view.state.tr.setSelection(
                view.state.selection.constructor.create(
                  view.state.doc,
                  nodePos,
                  nodePos + node.nodeSize
                ) as any
              )
              view.dispatch(tr.setSelection(selection.selection))
              return true
            }

            return false
          },
        },
      }),
    ]
  },
})

export default SourceCitation
